import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_FLOW, SERVICE_TYPES, URGENCY_LEVELS } from "@/lib/constants";
import { Search, Loader2, ChevronRight, RefreshCw, UserPlus, CheckCircle2, Archive, Hammer } from "lucide-react";
import { toast } from "sonner";
import NewJobModal from "@/components/NewJobModal";

// Categories drive what shows up by default. "Active" excludes Completed (and
// Cancelled if it ever exists) so the workshop floor view stays focused.
const DONE_STATUSES = ["Completed", "Cancelled"];
const isDone = (s) => DONE_STATUSES.includes(s);
const ACTIVE_STATUSES = STATUS_FLOW.filter((s) => !isDone(s));
const ALL_STATUSES = [...STATUS_FLOW, ...DONE_STATUSES.filter((s) => !STATUS_FLOW.includes(s))];

const CATEGORIES = [
  { key: "active", label: "Active", icon: Hammer, default: true, hint: "Everything that's not done yet" },
  { key: "completed", label: "Completed", icon: CheckCircle2, hint: "Finished or cancelled" },
  { key: "all", label: "All", icon: Archive, hint: "Including archived" },
];

const AdminJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("active");
  const [filters, setFilters] = useState({ status: "", urgency: "", service_type: "", search: "", sort: "-created_at" });
  const [newOpen, setNewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Backend filtering on status is exact-match; for category filtering we fetch
      // all and slice client-side so the status pills update in real time without
      // an extra round-trip.
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/admin/jobs", { params });
      setJobs(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters]); // eslint-disable-line

  const changeCategory = (key) => {
    setCategory(key);
    // Clear sub-status filter if it doesn't fit the new category.
    if ((key === "completed" && filters.status && !isDone(filters.status)) ||
        (key === "active" && isDone(filters.status))) {
      setFilters((s) => ({ ...s, status: "" }));
    }
  };

  // Apply category filter on top of whatever the backend returned.
  const visibleJobs = useMemo(() => {
    if (category === "completed") return jobs.filter((j) => isDone(j.status));
    if (category === "active") return jobs.filter((j) => !isDone(j.status));
    return jobs;
  }, [jobs, category]);

  const counts = useMemo(() => {
    const all = jobs.length;
    const active = jobs.filter((j) => !isDone(j.status)).length;
    const completed = jobs.filter((j) => isDone(j.status)).length;
    const byStatus = {};
    ALL_STATUSES.forEach((s) => { byStatus[s] = jobs.filter((j) => j.status === s).length; });
    return { all, active, completed, byStatus };
  }, [jobs]);

  const visibleStatusPills = category === "completed" ? DONE_STATUSES : category === "active" ? ACTIVE_STATUSES : ALL_STATUSES;

  return (
    <AdminLayout
      eyebrow="Workshop floor"
      title="Jobs"
      actions={
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-400" data-testid="new-job-btn">
            <UserPlus className="size-3.5" /> New booking
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-white/5 border border-white/10 text-xs uppercase tracking-wider font-semibold text-slate-200 hover:bg-white/10" data-testid="refresh-btn">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Top-level categories — keeps Completed jobs out of the way unless you ask for them */}
        <div className="flex flex-wrap gap-2" data-testid="category-tabs">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.key;
            const count = c.key === "active" ? counts.active : c.key === "completed" ? counts.completed : counts.all;
            return (
              <button
                key={c.key}
                onClick={() => changeCategory(c.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-xs uppercase tracking-wider font-bold border transition ${active ? "bg-spark-teal text-white border-spark-teal" : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08]"}`}
                data-testid={`category-${c.key}`}
                title={c.hint}
              >
                <Icon className="size-3.5" />
                {c.label}
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/[0.08] text-slate-400"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Sub-status pills inside the current category */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilters((s) => ({ ...s, status: "" }))}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition-colors ${!filters.status ? "bg-spark-yellow text-spark-ink border-spark-yellow" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
            data-testid="filter-status-all"
          >
            All {category === "active" ? "active" : category === "completed" ? "completed" : ""} <span className="ml-1.5 opacity-70">{category === "active" ? counts.active : category === "completed" ? counts.completed : counts.all}</span>
          </button>
          {visibleStatusPills.map((s) => (
            <button
              key={s}
              onClick={() => setFilters((f) => ({ ...f, status: s }))}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition-colors ${filters.status === s ? "bg-spark-yellow text-spark-ink border-spark-yellow" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
              data-testid={`filter-status-${s}`}
            >
              {s} <span className="ml-1.5 opacity-70">{counts.byStatus[s] || 0}</span>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="rounded-xl bg-white/[0.04] border border-white/[0.07] p-3 sm:p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                placeholder="Search ref, name, email…"
                className="w-full bg-spark-teal-night border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-spark-teal"
                value={filters.search}
                onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))}
                data-testid="filter-search"
              />
            </div>
            <select
              className="bg-spark-teal-night border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-spark-teal"
              value={filters.service_type}
              onChange={(e) => setFilters((s) => ({ ...s, service_type: e.target.value }))}
              data-testid="filter-service"
            >
              <option value="">All services</option>
              {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              className="bg-spark-teal-night border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-spark-teal"
              value={filters.urgency}
              onChange={(e) => setFilters((s) => ({ ...s, urgency: e.target.value }))}
              data-testid="filter-urgency"
            >
              <option value="">All urgency</option>
              {URGENCY_LEVELS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
            <select
              className="bg-spark-teal-night border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-spark-teal"
              value={filters.sort}
              onChange={(e) => setFilters((s) => ({ ...s, sort: e.target.value }))}
              data-testid="filter-sort"
            >
              <option value="-created_at">Newest first</option>
              <option value="created_at">Oldest first</option>
              <option value="urgency">Urgency</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>

        {/* Jobs table — Status moved to col 1 so the workshop floor sees state first */}
        <div className="rounded-xl bg-white/[0.04] border border-white/[0.07] overflow-hidden">
          {loading && jobs.length === 0 ? (
            <div className="text-center py-20 text-slate-400"><Loader2 className="size-5 animate-spin inline" /></div>
          ) : visibleJobs.length === 0 ? (
            <p className="text-center py-16 text-slate-400" data-testid="no-jobs">
              {category === "completed" ? "No completed jobs yet." : category === "active" ? "Nothing on the floor right now — go grab a coffee ☕" : "No bookings match these filters."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left bg-white/[0.03]">
                    <th className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-4 py-3">Status</th>
                    <th className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-4 py-3">Customer</th>
                    <th className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-4 py-3">Scooter</th>
                    <th className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-4 py-3">Service</th>
                    <th className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-4 py-3">Ref</th>
                    <th className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-4 py-3">Booked</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleJobs.map((j) => (
                    <tr
                      key={j.id}
                      className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer"
                      onClick={() => navigate(`/admin/jobs/${j.id}`)}
                      data-testid={`job-row-${j.ref_number}`}
                    >
                      <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                      <td className="px-4 py-3">
                        <div className="text-white font-semibold">{j.full_name}</div>
                        <div className="text-xs text-slate-500">{j.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-200">
                        {j.scooter_brand} <span className="text-slate-500">{j.scooter_model}</span>
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-200">{j.service_type}</td>
                      <td className="px-4 py-3 font-display uppercase tracking-wider font-bold text-spark-yellow">{j.ref_number}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {new Date(j.created_at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-3 text-right pr-5"><ChevronRight className="size-4 text-slate-500" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <NewJobModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={() => load()} />
    </AdminLayout>
  );
};

export default AdminJobs;
